import React, { useEffect, useMemo, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import {
  createStaffUser,
  deleteStaffUser,
  getMenuCatalog,
  listStaffUsers,
  updateStaffUser,
} from '../../api/superadmin';
import './SuperadminStaffUsersPage.css';

const emptyForm = { username: '', password: '', is_active: true, menu_keys: [] };

const SuperadminStaffUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = pas de formulaire ouvert
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([listStaffUsers(), getMenuCatalog()])
      .then(([usersData, catalogData]) => {
        setUsers(usersData);
        setCatalog(catalogData);
      })
      .catch(() => setError('Impossible de charger les utilisateurs.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const menuKeySet = useMemo(() => new Set(form.menu_keys), [form.menu_keys]);

  const openCreate = () => {
    setEditingId('new');
    setForm(emptyForm);
    setError('');
  };

  const openEdit = (user) => {
    setEditingId(user.id);
    setForm({
      username: user.username,
      password: '',
      is_active: user.is_active,
      menu_keys: user.menu_keys || [],
    });
    setError('');
  };

  const closeForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const toggleKey = (key) => {
    setForm((prev) => {
      const set = new Set(prev.menu_keys);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, menu_keys: Array.from(set) };
    });
  };

  const toggleSection = (items, checkAll) => {
    setForm((prev) => {
      const set = new Set(prev.menu_keys);
      items.forEach((it) => (checkAll ? set.add(it.key) : set.delete(it.key)));
      return { ...prev, menu_keys: Array.from(set) };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId === 'new') {
        const created = await createStaffUser({
          username: form.username,
          password: form.password,
          is_active: form.is_active,
          menu_keys: form.menu_keys,
        });
        setUsers((prev) => [...prev, created]);
      } else {
        const payload = {
          is_active: form.is_active,
          menu_keys: form.menu_keys,
        };
        if (form.password) payload.password = form.password;
        const updated = await updateStaffUser(editingId, payload);
        setUsers((prev) => prev.map((u) => (u.id === editingId ? updated : u)));
      }
      closeForm();
    } catch (err) {
      let msg = 'Sauvegarde impossible.';
      try {
        const parsed = JSON.parse(err.message);
        const detail = Object.values(parsed).flat().join(' ');
        if (detail) msg = `${msg} — ${detail}`;
      } catch (_) {
        if (err.message) msg = `${msg} — ${err.message}`;
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer l'utilisateur "${user.username}" ?`)) return;
    setError('');
    try {
      await deleteStaffUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      if (editingId === user.id) closeForm();
    } catch (_) {
      setError("Suppression impossible pour cet utilisateur.");
    }
  };

  return (
    <SuperadminLayout>
      <h2 className="ssu-title">Utilisateurs staff (acces limite)</h2>
      <p className="ssu-hint">
        Ces comptes n&apos;ont acces qu&apos;aux pages du portail company cochees ci-dessous, et
        uniquement en mode &laquo;&nbsp;view d&apos;une company&nbsp;&raquo; (impersonation).
      </p>

      {error && <p className="ssu-error">{error}</p>}

      {editingId === null && (
        <button type="button" className="ssu-new-btn" onClick={openCreate}>
          + Nouvel utilisateur
        </button>
      )}

      {editingId !== null && (
        <form onSubmit={handleSave} className="ssu-form">
          <h3 className="ssu-form-title">
            {editingId === 'new' ? 'Nouvel utilisateur' : `Modifier : ${form.username}`}
          </h3>

          <div className="ssu-form-row">
            <label className="ssu-label">
              Username
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                disabled={editingId !== 'new'}
                required
              />
            </label>
            <label className="ssu-label">
              {editingId === 'new' ? 'Mot de passe' : 'Nouveau mot de passe (optionnel)'}
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                required={editingId === 'new'}
              />
            </label>
            <label className="ssu-label ssu-checkbox-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
              Actif
            </label>
          </div>

          <div className="ssu-catalog">
            {catalog.map((section) => (
              <div key={section.section} className="ssu-section">
                <div className="ssu-section-header">
                  <strong>{section.section}</strong>
                  <span className="ssu-section-actions">
                    <button type="button" onClick={() => toggleSection(section.items, true)}>
                      Tout cocher
                    </button>
                    <button type="button" onClick={() => toggleSection(section.items, false)}>
                      Tout decocher
                    </button>
                  </span>
                </div>
                <div className="ssu-section-items">
                  {section.items.map((item) => (
                    <label key={item.key} className="ssu-item-checkbox">
                      <input
                        type="checkbox"
                        checked={menuKeySet.has(item.key)}
                        onChange={() => toggleKey(item.key)}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="ssu-form-actions">
            <button type="submit" className="ssu-save-btn" disabled={saving}>
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            <button type="button" className="ssu-cancel-btn" onClick={closeForm} disabled={saving}>
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <table className="ssu-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Statut</th>
              <th>Acces</th>
              <th>Cree le</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.is_active ? 'Actif' : 'Inactif'}</td>
                <td>{(user.menu_keys || []).length} page(s)</td>
                <td>{user.date_joined ? new Date(user.date_joined).toLocaleDateString() : '—'}</td>
                <td>
                  <div className="ssu-actions">
                    <button type="button" className="ssu-edit-btn" onClick={() => openEdit(user)}>
                      Modifier
                    </button>
                    <button type="button" className="ssu-delete-btn" onClick={() => handleDelete(user)}>
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="ssu-empty">Aucun utilisateur staff cree.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </SuperadminLayout>
  );
};

export default SuperadminStaffUsersPage;
